import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { SUPPORTED_LANGUAGES } from '../i18n.js';

export default function Profile() {
  const { t, i18n } = useTranslation();
  const { user, profile, profileError, signOut, updateProfile, loading, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [affiliation, setAffiliation] = useState('');
  const [researchGoals, setResearchGoals] = useState('');
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name ?? '');
      setPreferredLanguage(profile.preferred_language ?? 'en');
      setAffiliation(profile.affiliation ?? '');
      setResearchGoals(profile.research_goals ?? '');
      setNewsletterOptIn(profile.newsletter_opt_in ?? true);
    }
  }, [profile]);

  async function handleSave(event) {
    event.preventDefault();
    setError(null);
    setSavedAt(null);
    setSaving(true);
    try {
      await updateProfile({
        display_name: displayName.trim() || null,
        preferred_language: preferredLanguage,
        affiliation: affiliation.trim() || null,
        research_goals: researchGoals.trim() || null,
        newsletter_opt_in: newsletterOptIn,
      });
      if (preferredLanguage !== i18n.resolvedLanguage) {
        i18n.changeLanguage(preferredLanguage);
      }
      setSavedAt(Date.now());
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    navigate('/');
  }

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <h1 className="font-serif text-3xl text-gold-light">{t('profile.missing.title')}</h1>
        <p className="mt-3 text-ink-secondary">
          {profileError ?? t('profile.missing.body')}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button type="button" className="btn-primary" onClick={refreshProfile}>
            {t('profile.missing.retry')}
          </button>
          <button type="button" className="btn-ghost" onClick={handleSignOut}>
            {t('nav.logout')}
          </button>
        </div>
      </div>
    );
  }

  const statusLabel = t(`profile.statusLabel.${profile.status}`);
  const roleLabel = t(`profile.roleLabel.${profile.role}`);

  return (
    <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
      <header className="mb-10">
        <div className="section-label">{t('nav.profile')}</div>
        <h1 className="font-serif text-4xl font-semibold text-cream">
          {profile.display_name || profile.email}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Badge tone={profile.status === 'active' ? 'positive' : 'pending'}>
            {statusLabel}
          </Badge>
          <Badge tone={profile.role === 'admin' ? 'admin' : 'default'}>
            {roleLabel}
          </Badge>
        </div>
      </header>

      {profile.status === 'pending_approval' && (
        <div className="mb-8 rounded-md border border-gold/30 bg-navy-mid/30 p-5">
          <h2 className="font-serif text-lg text-gold-light">
            {t('profile.pendingApproval.title')}
          </h2>
          <p className="mt-2 text-sm text-ink-secondary">
            {t('profile.pendingApproval.body')}
          </p>
        </div>
      )}

      <form onSubmit={handleSave} className="flex flex-col gap-5">
        <Field label={t('profile.email')}>
          <input
            type="email"
            value={user?.email ?? profile.email}
            disabled
            className="w-full cursor-not-allowed rounded-md border border-white/10 bg-navy-deep/40 px-4 py-2.5 text-ink-secondary"
          />
        </Field>

        <Field label={t('profile.displayName')}>
          <input
            type="text"
            value={displayName}
            placeholder={t('profile.displayNamePlaceholder')}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2.5 text-cream placeholder:text-ink-muted focus:border-gold/50"
          />
        </Field>

        <Field label={t('profile.affiliation')}>
          <input
            type="text"
            autoComplete="organization"
            maxLength={200}
            value={affiliation}
            placeholder={t('profile.affiliationPlaceholder')}
            onChange={(e) => setAffiliation(e.target.value)}
            className="w-full rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2.5 text-cream placeholder:text-ink-muted focus:border-gold/50"
          />
        </Field>

        <Field label={t('profile.researchGoals')} hint={t('profile.researchGoalsHint')}>
          <textarea
            rows={4}
            maxLength={1000}
            value={researchGoals}
            placeholder={t('profile.researchGoalsPlaceholder')}
            onChange={(e) => setResearchGoals(e.target.value)}
            className="w-full resize-y rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2.5 text-cream placeholder:text-ink-muted focus:border-gold/50"
          />
        </Field>

        <Field label={t('profile.preferredLanguage')}>
          <select
            value={preferredLanguage}
            onChange={(e) => setPreferredLanguage(e.target.value)}
            className="w-full rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2.5 text-cream focus:border-gold/50"
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.flag} {lang.name}
              </option>
            ))}
          </select>
        </Field>

        <div id="newsletter" className="rounded-md border border-white/10 bg-navy-deep/40 p-4">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={newsletterOptIn}
              onChange={(e) => setNewsletterOptIn(e.target.checked)}
              className="mt-1 h-4 w-4 accent-gold"
            />
            <span>
              <span className="block text-sm font-medium text-cream">{t('profile.newsletter.label')}</span>
              <span className="mt-0.5 block text-xs text-ink-secondary">{t('profile.newsletter.hint')}</span>
            </span>
          </label>
        </div>

        {error && (
          <p className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">
            {error}
          </p>
        )}
        {savedAt && (
          <p className="rounded-md border border-emerald-500/30 bg-emerald-950/30 px-3 py-2 text-sm text-emerald-200">
            {t('profile.saved')}
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? t('common.loading') : t('common.save')}
          </button>
          <button type="button" className="btn-ghost" onClick={handleSignOut}>
            {t('nav.logout')}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-muted">{hint}</span>}
    </label>
  );
}

function Badge({ tone = 'default', children }) {
  const tones = {
    default: 'border-white/15 bg-navy-mid/40 text-ink-secondary',
    positive: 'border-emerald-500/40 bg-emerald-950/30 text-emerald-200',
    pending: 'border-gold/40 bg-navy-mid/40 text-gold-light',
    admin: 'border-blue-accent/50 bg-navy-mid/40 text-blue-glow',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-[0.12em] ${tones[tone]}`}
    >
      {children}
    </span>
  );
}
